import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Drawer, Typography, Button, Tag, Slider } from 'antd';
import { ReloadOutlined, ArrowLeftOutlined, LockOutlined, CrownOutlined, PieChartOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useScopedNavigate } from '../MarketerView/useScopedNavigate';
import * as d3 from 'd3';
import { IBrandEquityScoreResponse } from '../../services/nestjs-base-api';
import { useGetSystemSettingQuery, useReanalyzeElementMutation } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import BrandEquityD3Visualization from './BrandEquityD3Visualization';
import RadialOrbitalVisualization from './RadialOrbitalVisualization';
import BrandEquityVisualizationV3 from './BrandEquityVisualizationV3';
import { CardsVisualization } from './CardsVisualization';
import StatementDetailModal from './StatementDetailModal';
import ReactMarkdown from 'react-markdown';
import { getDisplayName } from '../../utils/elementDisplayNames';

const { Title, Text } = Typography;

// Helper function to create gradient slider with position indicator
const createBipolarProgressBar = (score: number | null, backgroundColor: string, fillColor: string, elementName?: string) => {
    // Handle null scores (no data available)
    if (score === null) {
        return `
            <div style="
                display: flex;
                align-items: center;
                gap: 8px;
                height: 32px;
            ">
                <div style="
                    position: relative;
                    width: 100px;
                    height: 16px;
                    display: flex;
                    align-items: center;
                ">
                    <div style="
                        width: 100%;
                        height: 8px;
                        background: #e5e7eb;
                        border-radius: 4px;
                        border: 1px solid rgba(0, 0, 0, 0.1);
                    "></div>
                </div>
                <div style="
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    height: 26px;
                    padding: 0 4px;
                    color: #8c8c8c;
                    font-size: 14px;
                    font-weight: 500;
                    font-style: italic;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                ">N/A</div>
            </div>
        `;
    }

    const isNegative = score < 0;
    const absScore = Math.abs(score);

    // Calculate visual elements
    const barWidth = 100;
    const barHeight = 8;
    const displayScore = score > 0 ? `+${score.toFixed(0)}%` : score === 0 ? '0%' : `${score.toFixed(0)}%`;

    // Calculate indicator position based on element type
    let indicatorPosition: number;
    if (elementName === 'Price Deals') {
        // Price Deals ranges from -100% to 0%, so map to 0 to 100px
        indicatorPosition = ((score + 100) / 100) * barWidth;
    } else {
        // Standard bipolar scale from -100% to +100%, map to 0 to 100px
        indicatorPosition = ((score + 100) / 200) * barWidth;
    }

    // Make background much more visible
    let enhancedBackground = backgroundColor;
    if (backgroundColor.includes('0.2)')) {
        enhancedBackground = backgroundColor.replace('0.2)', '0.6)'); // Very visible
    } else {
        // Fallback: create a visible background based on fillColor
        enhancedBackground = `${fillColor}60`; // 38% opacity using hex notation
    }

    // Create gradient slider with position indicator
    const svgElement = `
        <div style="
            display: flex;
            align-items: center;
            gap: 8px;
            height: 32px;
        ">
            <!-- Gradient Slider Container -->
            <div style="
                position: relative;
                width: ${barWidth}px;
                height: ${barHeight + 8}px;
                display: flex;
                align-items: center;
            ">
                <!-- Gradient Track -->
                <div style="
                    position: relative;
                    width: 100%;
                    height: ${barHeight}px;
                    background: ${elementName === 'Price Deals' ?
                        'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #eab308 60%, #84cc16 85%, #22c55e 100%)' :
                        'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)'
                    };
                    border-radius: 4px;
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
                ">
                    <!-- Zero marker line -->
                    <div style="
                        position: absolute;
                        left: ${elementName === 'Price Deals' ? '100%' : '50%'};
                        top: -1px;
                        bottom: -1px;
                        width: 1px;
                        background: rgba(0, 0, 0, 0.4);
                        transform: translateX(-50%);
                        border-radius: 1px;
                    "></div>
                </div>

                <!-- Position Indicator -->
                <div style="
                    position: absolute;
                    left: ${indicatorPosition}px;
                    top: -3px;
                    width: 3px;
                    height: ${barHeight + 10}px;
                    background: #1f2937;
                    border-radius: 2px;
                    transform: translateX(-50%);
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
                    transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                "></div>
            </div>

            <!-- Clean Number Display -->
            <div style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                min-width: 50px;
                height: 26px;
                padding: 0 4px;
                color: #1f2937;
                font-size: 14px;
                font-weight: 500;
                letter-spacing: -0.02em;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                transition: all 0.3s ease;
            ">
                ${displayScore}
            </div>
        </div>
    `;

    return svgElement;
};

const StrengthSlider = ({ value, label }: { value: number | null; label?: string }) => {
    useEffect(() => {
        // Add custom styles to hide the track elements and add midline
        const styleId = 'strength-slider-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                .strength-slider .ant-slider-track,
                .strength-slider .ant-slider-step,
                .strength-slider .ant-slider-dot {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                }
                .strength-slider .ant-slider-handle::after,
                .strength-slider .ant-slider-handle::before,
                .strength-slider .ant-slider-handle-dragging::after,
                .strength-slider .ant-slider-handle-dragging::before {
                    display: none !important;
                    content: none !important;
                    width: 0 !important;
                    height: 0 !important;
                    box-shadow: none !important;
                    background: none !important;
                }
                .strength-slider {
                    position: relative;
                }
                .strength-slider::after {
                    content: '';
                    position: absolute;
                    top: 4px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 2px;
                    height: 20px;
                    background-color: #000;
                    opacity: 0.3;
                    z-index: 5;
                    pointer-events: none;
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    // All statements now use the -100 to +100 scale
    // Map the value to slider position (0-100 range for display)
    const sliderValue = ((value || 0) + 100) / 2;

    return (
        <div style={{ width: '100%' }}>
            {label && <div style={{ marginBottom: '8px', fontSize: '14px' }}>{label}</div>}
            <Slider
                value={sliderValue}
                disabled
                min={0}
                max={100}
                tooltip={{ open: false }}
                className="strength-slider"
                style={{ margin: '12px 0 8px 0' }}
                styles={{
                    track: {
                        display: 'none !important',
                        visibility: 'hidden',
                        opacity: 0,
                        background: 'transparent',
                        border: 'none'
                    },
                    rail: {
                        background: value === null
                            ? '#6b7280'
                            : 'linear-gradient(to right, #ef4444, #eab308, #22c55e)',
                        height: '20px',
                        borderRadius: '5px',
                        border: '1px solid #374151',
                    },
                    handle: {
                        backgroundColor: '#000',
                        border: 'none',
                        width: '4px',
                        height: '30px',
                        borderRadius: '2px',
                        marginTop: '-5px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        opacity: value === null ? 0 : 1,
                        visibility: value === null ? 'hidden' : 'visible',
                        zIndex: 10
                    }
                }}
            />
        </div>
    );
};

const ResponseSpeedometer = ({ responseCount }: { responseCount: number }) => {
    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const width = 200;
        const height = 120;
        const radius = 80;

        const g = svg.append('g')
            .attr('transform', `translate(${width / 2}, ${height - 10})`);

        // Define angle range (180 degrees for semi-circle)
        const startAngle = -Math.PI;
        const endAngle = 0;

        // Create gradient definition for both outline and fill
        const defs = svg.append('defs');
        const gradient = defs.append('linearGradient')
            .attr('id', 'gauge-gradient')
            .attr('gradientUnits', 'userSpaceOnUse')
            .attr('x1', -radius)
            .attr('y1', 0)
            .attr('x2', radius)
            .attr('y2', 0);

        gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', '#f97316'); // Orange

        gradient.append('stop')
            .attr('offset', '50%')
            .attr('stop-color', '#eab308'); // Yellow

        gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', '#22c55e'); // Green

        // Create arc generator for filled portion
        const arc = d3.arc()
            .innerRadius(radius - 20)
            .outerRadius(radius)
            .startAngle(startAngle);

        // Draw gauge outline (full 180 degrees) - create manually
        const outerRadius = radius;
        const innerRadius = radius - 20;

        // Create full semicircle path manually
        const pathData = [
            `M ${-outerRadius} 0`, // Move to leftmost point of outer arc
            `A ${outerRadius} ${outerRadius} 0 0 1 ${outerRadius} 0`, // Outer arc from left to right
            `L ${innerRadius} 0`, // Line to inner right
            `A ${innerRadius} ${innerRadius} 0 0 0 ${-innerRadius} 0`, // Inner arc from right to left
            `Z` // Close path
        ].join(' ');

        g.append('path')
            .attr('d', pathData)
            .style('fill', 'url(#gauge-gradient)')
            .style('opacity', 0.3)
            .style('stroke', '#9ca3af')
            .style('stroke-width', 1);

        // Draw filled portion with gradient
        if (responseCount > 0) {
            const maxScale = 60; // Max scale for display
            const fillAngle = startAngle + (Math.min(responseCount, maxScale) / maxScale) * Math.PI;
            g.append('path')
                .datum({ endAngle: fillAngle })
                .attr('d', arc as any)
                .style('fill', 'url(#gauge-gradient)')
                .style('opacity', 0.9);
        }

        // Add segment labels
        g.append('text')
            .attr('x', -radius + 10)
            .attr('y', -5)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6b7280')
            .style('font-weight', '600')
            .text('Low');

        g.append('text')
            .attr('x', 0)
            .attr('y', -radius + 15)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6b7280')
            .style('font-weight', '600')
            .text('Moderate');

        g.append('text')
            .attr('x', radius - 10)
            .attr('y', -5)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6b7280')
            .style('font-weight', '600')
            .text('Good');

        // Add subtle tick marks (without numbers)
        const maxScale = 60; // Max scale for display
        const ticks = [0, 20, 40];
        ticks.forEach(tick => {
            const angle = startAngle + (tick / maxScale) * Math.PI;
            const x1 = Math.cos(angle) * (radius - 20);
            const y1 = Math.sin(angle) * (radius - 20);
            const x2 = Math.cos(angle) * (radius - 25);
            const y2 = Math.sin(angle) * (radius - 25);

            g.append('line')
                .attr('x1', x1)
                .attr('y1', y1)
                .attr('x2', x2)
                .attr('y2', y2)
                .style('stroke', '#9ca3af')
                .style('stroke-width', 1.5);
        });

        // Add needle
        const needleAngle = startAngle + (Math.min(responseCount, maxScale) / maxScale) * Math.PI;
        const needleLength = radius - 25;

        // Needle shadow
        g.append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', Math.cos(needleAngle) * needleLength)
            .attr('y2', Math.sin(needleAngle) * needleLength)
            .style('stroke', 'rgba(0,0,0,0.2)')
            .style('stroke-width', 4)
            .style('stroke-linecap', 'round')
            .attr('transform', 'translate(1, 1)');

        // Needle
        g.append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', Math.cos(needleAngle) * needleLength)
            .attr('y2', Math.sin(needleAngle) * needleLength)
            .style('stroke', '#1f2937')
            .style('stroke-width', 3)
            .style('stroke-linecap', 'round');

        // Center dot
        g.append('circle')
            .attr('r', 6)
            .style('fill', '#1f2937');

        // Response count display
        g.append('text')
            .attr('x', 0)
            .attr('y', 25)
            .attr('text-anchor', 'middle')
            .style('font-size', '18px')
            .style('font-weight', 'bold')
            .style('fill', '#1f2937')
            .text(responseCount);

        g.append('text')
            .attr('x', 0)
            .attr('y', 40)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6b7280')
            .text('responses');

    }, [responseCount]);

    const getStatus = () => {
        if (responseCount <= 20) return { text: 'Low Response Count', color: '#ef4444' };
        if (responseCount <= 40) return { text: 'Moderate Response Count', color: '#eab308' };
        return { text: 'Good Response Count', color: '#22c55e' };
    };

    const status = getStatus();

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            width: '100%'
        }}>
            <svg ref={svgRef} width={200} height={120} />
            <div style={{
                marginTop: '8px',
                fontSize: '13px',
                fontWeight: '600',
                color: status.color
            }}>
                {status.text}
            </div>
            {responseCount <= 20 && (
                <div style={{
                    marginTop: '8px',
                    fontSize: '11px',
                    color: '#6b7280',
                    textAlign: 'center',
                    maxWidth: '180px'
                }}>
                    Results may not be statistically reliable
                </div>
            )}
        </div>
    );
};

interface BrandEquityVisualizationWrapperProps {
    results: IBrandEquityScoreResponse;
    isModal?: boolean;
    isAdmin?: boolean;
    organisationId?: number;
    brandId?: number; // For brand-level override
    forceVisualizationType?: 'classic' | 'orbital' | 'orbital-v3' | 'cards'; // Force specific visualization type
    initialSelectedElement?: string | null;
}

interface Node {
    id: string;
    name: string;
    type: 'top' | 'latent' | 'qc';
    level: number;
    score?: number;
}

const BrandEquityVisualizationWrapper: React.FC<BrandEquityVisualizationWrapperProps> = ({
    results,
    isModal = false,
    isAdmin = false,
    organisationId,
    brandId,
    forceVisualizationType,
    initialSelectedElement = null,
}) => {
    const { user } = useAuth();
    const intl = useIntl();
    const navigate = useScopedNavigate();

    // Drawer state
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerData, setDrawerData] = useState<any>(null);
    const [statementModalVisible, setStatementModalVisible] = useState(false);
    const [selectedStatement, setSelectedStatement] = useState<any>(null);
    const [selectedStatementCategory, setSelectedStatementCategory] = useState<string>('');
    const [currentAiSummary, setCurrentAiSummary] = useState<string | undefined>(undefined);
    const [isReanalyzing, setIsReanalyzing] = useState(false);

    // Navigation history state
    const [navigationHistory, setNavigationHistory] = useState<any[]>([]);
    const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);

    // Focused element state for highlighting on visualization
    const [focusedElementName, setFocusedElementName] = useState<string | null>(null);


    // Determine visualization type - default to orbital-v3 if not forced
    const visualizationType = useMemo(() => {
        // If forceVisualizationType is provided, use it
        if (forceVisualizationType) {
            return forceVisualizationType;
        }

        // Default to orbital-v3
        return 'orbital-v3';
    }, [forceVisualizationType]);

    // Re-analyze element mutation
    const [reanalyzeElement] = useReanalyzeElementMutation();

    // Fetch element descriptions from system settings
    const { data: elementDescriptionsData } = useGetSystemSettingQuery({
        jwtToken: user?.jwtToken || '',
        settingName: 'ELEMENT_DESCRIPTIONS'
    }, {
        skip: !user?.jwtToken
    });

    // Parse element descriptions
    const elementDescriptions = useMemo(() => {
        try {
            if (elementDescriptionsData) {
                let parsed;

                // Handle different response formats
                if (typeof elementDescriptionsData === 'string') {
                    // Try to parse the string
                    try {
                        parsed = JSON.parse(elementDescriptionsData);

                        // Check if the result is still a string (double-encoded JSON)
                        if (typeof parsed === 'string') {
                            parsed = JSON.parse(parsed);
                        }
                    } catch (parseError) {
                        console.error('Failed to parse elementDescriptionsData string:', parseError);
                        return {};
                    }
                } else if (typeof elementDescriptionsData === 'object') {
                    parsed = elementDescriptionsData;
                } else {
                    return {};
                }

                // Handle different possible structures
                if (parsed && typeof parsed === 'object') {
                    // If it has an 'elements' property, use that
                    if (parsed.elements && typeof parsed.elements === 'object') {
                        return parsed.elements;
                    }
                    // If it has a 'value' property (API might wrap the data), try to parse that
                    else if (parsed.value) {
                        try {
                            const valueData = typeof parsed.value === 'string' ? JSON.parse(parsed.value) : parsed.value;
                            return valueData.elements || {};
                        } catch (e) {
                            console.error('Failed to parse value property:', e);
                        }
                    }
                    // If the parsed object itself looks like the elements structure, use it directly
                    else if (Object.keys(parsed).length > 0 && !parsed.elements) {
                        // Check if it looks like an elements object (has properties with mouseover/sidebar)
                        const firstKey = Object.keys(parsed)[0];
                        if (parsed[firstKey] && (parsed[firstKey].mouseover !== undefined || parsed[firstKey].sidebar !== undefined)) {
                            return parsed;
                        }
                    }
                }

                return {};
            }
        } catch (error) {
            console.error('Failed to parse element descriptions:', error);
        }
        return {};
    }, [elementDescriptionsData]);

    // Connection data for finding inputs/outputs
    const connectionData = useMemo(() => {
        const connections: Array<{
            input: string;
            inputType: string;
            latentElement: string;
        }> = [];

        results.latentElementScores.forEach(le => {
            le.inputs.forEach(input => {
                connections.push({
                    input: input.name,
                    inputType: input.type,
                    latentElement: le.element
                });
            });
        });

        return connections;
    }, [results]);

    // Build scores map
    const scoresMap = useMemo(() => {
        const map: Record<string, number> = {};
        map['Brand Equity'] = results.overallBrandEquityScore;
        results.latentElementScores.forEach(le => {
            if (le.score != null) {
                map[le.element] = le.score;
            }
        });
        results.questionCategoryScores.forEach(qc => {
            if (qc.meanScore !== null) {
                map[qc.category] = qc.meanScore;
            }
        });
        return map;
    }, [results]);

    // Helper function to determine element type
    const getElementType = (elementName: string): 'top' | 'latent' | 'qc' => {
        if (elementName === 'Brand Equity') return 'top';
        const isLatent = results.latentElementScores.some(le => le.element === elementName);
        return isLatent ? 'latent' : 'qc';
    };

    // Helper function to check if an element is locked
    const isElementLocked = (elementName: string): boolean => {
        const latentElement = results.latentElementScores.find(le => le.element === elementName);
        if (latentElement) return latentElement.locked === true;
        const qcElement = results.questionCategoryScores.find(qc => qc.category === elementName);
        if (qcElement) return qcElement.locked === true;
        return false;
    };

    // Get colors for element
    const getElementColors = (elementName: string, type: 'top' | 'latent' | 'qc') => {
        const backgroundColor = type === 'top' ? 'rgba(251, 191, 36, 0.2)' :
            type === 'latent' ?
                (elementName === 'Attitudinal Loyalty' || elementName === 'Perceived Quality' || elementName === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' :
                 elementName === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                'rgba(59, 130, 246, 0.2)';
        const fillColor = type === 'top' ? '#fbbf24' :
            type === 'latent' ?
                (elementName === 'Attitudinal Loyalty' || elementName === 'Perceived Quality' || elementName === 'Behavioural Loyalty' ? '#fb923c' :
                 elementName === 'Brand Loyalty' ? '#10b981' : '#ef4444') :
                '#3b82f6';
        return { backgroundColor, fillColor };
    };

    // Get constituent details for drawer
    const getConstituentDetails = (elementName: string) => {
        const inputs: any[] = [];
        const outputs: any[] = [];

        // Find the element score
        const latentElementScore = results.latentElementScores.find(le => le.element === elementName);
        const qcScore = results.questionCategoryScores.find(qc => qc.category === elementName);

        // Check if element is locked
        const isLocked = latentElementScore?.locked === true || qcScore?.locked === true;

        // Find inputs (what feeds into this element)
        connectionData.forEach(connection => {
            if (connection.latentElement === elementName) {
                // Find the score for this input
                const inputScore = scoresMap[connection.input];
                const inputType = getElementType(connection.input);
                inputs.push({
                    name: connection.input,
                    type: inputType === 'top' ? 'BE' : inputType === 'latent' ? 'LE' : 'QC',
                    score: inputScore
                });
            }
        });

        // Find outputs (what this element feeds into)
        connectionData.forEach(connection => {
            if (connection.input === elementName) {
                const outputScore = scoresMap[connection.latentElement];
                const outputType = getElementType(connection.latentElement);
                outputs.push({
                    name: connection.latentElement,
                    type: outputType === 'top' ? 'BE' : outputType === 'latent' ? 'LE' : 'QC',
                    score: outputScore
                });
            }
        });

        return {
            inputs,
            outputs,
            score: scoresMap[elementName] || latentElementScore?.score || qcScore?.meanScore,
            aiSummary: latentElementScore?.aiSummary || qcScore?.aiSummary,
            statements: (qcScore?.input || []).map((s: any) => ({
                ...s,
                polarityPercentage: s.meanScore,
            })),
            type: getElementType(elementName),
            locked: isLocked,
            classificationLabel: latentElementScore?.classificationLabel ?? qcScore?.classificationLabel,
        };
    };

    // Navigation history functions
    const navigateToElement = (elementName: string, elementData?: any) => {
        const details = getConstituentDetails(elementName);
        const newData = {
            id: elementName,
            name: elementName,
            ...elementData,
            ...details  // Details should overwrite elementData to ensure correct structure (especially inputs with scores)
        };

        // Add current element to history if it exists and is different from the new one
        if (drawerData && drawerData.name !== elementName) {
            const newHistory = [...navigationHistory, drawerData];
            setNavigationHistory(newHistory);
            setCurrentHistoryIndex(newHistory.length); // Point to the new element position
        }

        // Navigate to new element
        setDrawerData(newData);
        setCurrentAiSummary(details.aiSummary);
        setDrawerVisible(true);

        // Highlight the element on the visualization
        setFocusedElementName(elementName);
    };

    const navigateBack = () => {
        if (navigationHistory.length > 0 && currentHistoryIndex > 0) {
            const previousElement = navigationHistory[currentHistoryIndex - 1];
            setCurrentHistoryIndex(currentHistoryIndex - 1);
            setDrawerData(previousElement);
            setCurrentAiSummary(previousElement.aiSummary);

            // Highlight the previous element on the visualization
            setFocusedElementName(previousElement.name);
        }
    };

    const clearNavigationHistory = () => {
        setNavigationHistory([]);
        setCurrentHistoryIndex(-1);
    };

    const canNavigateBack = navigationHistory.length > 0 && currentHistoryIndex > 0;

    // Handle element click from either visualization
    const handleElementClick = (elementName: string, elementData?: any) => {
        navigateToElement(elementName, elementData);
    };

    // Handle re-analyze
    const handleReanalyze = async () => {
        if (!drawerData || !user?.jwtToken || !organisationId) return;

        // Determine if this is a latent element or measure (question category)
        const isLatent = results.latentElementScores.some(le => le.element === drawerData.name);
        const elementType = isLatent ? 'latent' : 'measure';

        setIsReanalyzing(true);
        try {
            const result = await reanalyzeElement({
                jwtToken: user.jwtToken,
                organisationId,
                elementType,
                elementName: drawerData.name,
                brandEquityData: results
            }).unwrap();

            if (result.aiSummary) {
                setCurrentAiSummary(result.aiSummary);
            }
        } catch (error) {
            console.error('Failed to re-analyze element:', error);
        } finally {
            setIsReanalyzing(false);
        }
    };

    return (
        <div style={{
            width: '100%',
            height: isModal ? '100vh' : '100%',
            position: 'relative'
        }}>
            {results.insufficientSampleSize ? (
                // Show placeholder content when insufficient sample size
                (visualizationType === 'orbital' || visualizationType === 'orbital-v3') ? (
                    // Orbital view: Show preview with dummy data
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: '20px',
                        padding: '40px',
                        backgroundColor: '#f9fafb',
                    }}>
                        <div style={{
                            textAlign: 'center',
                            maxWidth: '500px'
                        }}>
                            <div style={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'rgba(245, 158, 11, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                            }}>
                                <PieChartOutlined style={{ fontSize: 36, color: '#F59E0B' }} />
                            </div>
                            <Title level={3} style={{ color: '#6b7280', marginBottom: '12px' }}>
                                Orbital Visualization Preview
                            </Title>
                            <Text style={{ fontSize: '16px', color: '#9ca3af', display: 'block', marginBottom: '20px' }}>
                                Collect more survey responses to see your brand equity elements displayed in an interactive orbital visualization.
                                Your brand's latent elements and question categories will orbit around the central brand equity score,
                                with connections showing how they influence each other.
                            </Text>
                            <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                                Currently: <strong>{results.totalResponses}</strong> {results.totalResponses === 1 ? 'response' : 'responses'} collected
                            </Text>
                        </div>
                    </div>
                ) : visualizationType === 'cards' ? (
                    // Cards view: Show simple message
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px',
                        backgroundColor: '#f9fafb'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            maxWidth: '500px',
                            padding: '40px',
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            border: '2px solid #e5e7eb'
                        }}>
                            <div style={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'rgba(245, 158, 11, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                            }}>
                                <PieChartOutlined style={{ fontSize: 36, color: '#F59E0B' }} />
                            </div>
                            <Title level={3} style={{ color: '#1f2937', marginBottom: '12px' }}>
                                Insufficient Survey Responses
                            </Title>
                            <Text style={{ fontSize: '16px', color: '#6b7280', display: 'block', marginBottom: '20px' }}>
                                Collect more survey responses to view your brand equity data in card format.
                                Each card will display detailed metrics for your latent elements and question categories.
                            </Text>
                            <div style={{
                                padding: '16px',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '8px',
                                marginTop: '16px'
                            }}>
                                <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                                    <strong>{results.totalResponses}</strong> {results.totalResponses === 1 ? 'response' : 'responses'} collected
                                </Text>
                            </div>
                        </div>
                    </div>
                ) : (
                    // Classic view: Use existing visualization (it might have its own handling)
                    <BrandEquityD3Visualization
                        results={results}
                        isModal={isModal}
                        isAdmin={isAdmin}
                        organisationId={organisationId}
                    />
                )
            ) : (
                // Sufficient sample size: Show normal visualizations
                visualizationType === 'orbital' ? (
                    <RadialOrbitalVisualization
                        results={results}
                        isModal={isModal}
                        onElementClick={handleElementClick}
                        elementDescriptions={elementDescriptions}
                        focusedElementName={focusedElementName}
                    />
                ) : visualizationType === 'orbital-v3' ? (
                    <BrandEquityVisualizationV3
                        results={results}
                        isModal={isModal}
                        onElementClick={handleElementClick}
                        elementDescriptions={elementDescriptions}
                        focusedElementName={focusedElementName}
                        initialSelectedElement={initialSelectedElement}
                        getElementDetails={getConstituentDetails}
                        onReanalyze={async (elementName: string) => {
                            if (!user?.jwtToken || !organisationId) return undefined;
                            const isLatent = results.latentElementScores.some(le => le.element === elementName);
                            const elementType = isLatent ? 'latent' : 'measure';
                            setIsReanalyzing(true);
                            try {
                                const result = await reanalyzeElement({
                                    jwtToken: user.jwtToken,
                                    organisationId,
                                    elementType,
                                    elementName,
                                    brandEquityData: results
                                }).unwrap();
                                return result.aiSummary;
                            } catch (error) {
                                console.error('Failed to re-analyze element:', error);
                                return undefined;
                            } finally {
                                setIsReanalyzing(false);
                            }
                        }}
                        isReanalyzing={isReanalyzing}
                        onUpgradeClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                        onStatementClick={(statement, elementName) => {
                            setSelectedStatement({ ...statement, polarityPercentage: statement.meanScore });
                            setSelectedStatementCategory(elementName);
                            setStatementModalVisible(true);
                        }}
                    />
                ) : visualizationType === 'cards' ? (
                    <CardsVisualization
                        results={results}
                        onElementClick={handleElementClick}
                        focusedElementName={focusedElementName}
                        isModal={isModal}
                    />
                ) : (
                    <BrandEquityD3Visualization
                        results={results}
                        isModal={isModal}
                        isAdmin={isAdmin}
                        organisationId={organisationId}
                    />
                )
            )}

            {/* Shared Drawer - show for orbital, orbital-v3 (in drawer mode), and cards visualizations */}
            {(visualizationType === 'orbital' || visualizationType === 'cards') && (
                <Drawer
                    placement="right"
                    closable={false}
                    onClose={() => {
                        setDrawerVisible(false);
                        setDrawerData(null);
                        clearNavigationHistory();
                        setFocusedElementName(null);
                    }}
                    open={drawerVisible}
                    width={410}
                    styles={{ header: { display: 'none' }, body: { padding: 0, display: 'flex', flexDirection: 'column', width: '100%' } }}
                >
                    {drawerData && (
                        <div style={{
                            width: '100%',
                            minWidth: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch'
                        }}>
                            {/* Button Row */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px 0 16px'
                            }}>
                                {canNavigateBack && (
                                    <Button
                                        type="text"
                                        icon={<ArrowLeftOutlined />}
                                        onClick={navigateBack}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '32px',
                                            height: '32px',
                                            minWidth: '32px',
                                            borderRadius: '6px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            border: '1px solid rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                                            e.currentTarget.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                    />
                                )}
                                <Button
                                    type="text"
                                    onClick={() => {
                                        setDrawerVisible(false);
                                        setDrawerData(null);
                                        clearNavigationHistory();
                                        setFocusedElementName(null);
                                    }}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        minWidth: '32px',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        color: '#6b7280',
                                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.2s ease',
                                        marginLeft: canNavigateBack ? '0' : 'auto'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                                        e.currentTarget.style.color = '#dc2626';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                                        e.currentTarget.style.color = '#6b7280';
                                    }}
                                >
                                    ✕
                                </Button>
                            </div>

                            {/* Title and Content Section */}
                            <div style={{
                                padding: '16px 20px 12px 20px',
                                borderBottom: drawerData.locked ? 'none' : '1px solid #f0f0f0',
                                marginBottom: drawerData.locked ? '0' : '16px',
                                textAlign: 'left',
                                width: '100%',
                                boxSizing: 'border-box',
                                alignSelf: 'stretch'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: drawerData.locked ? 'center' : 'flex-start',
                                    gap: '12px',
                                    padding: '8px 16px',
                                    backgroundColor: drawerData.locked
                                        ? 'rgba(156, 163, 175, 0.2)'
                                        : (() => {
                                            const { backgroundColor } = getElementColors(drawerData.name, drawerData.type);
                                            return backgroundColor.replace('0.2)', '0.3)');
                                        })(),
                                    borderRadius: '8px',
                                    marginBottom: '12px',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}>
                                    <div style={{ flex: drawerData.locked ? 'none' : 1 }}>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: drawerData.locked ? '#6b7280' : '#1f2937',
                                            marginBottom: '2px',
                                            textAlign: drawerData.locked ? 'center' : 'left'
                                        }}>
                                            {getDisplayName(drawerData.name)}
                                        </div>
                                    </div>
                                    {!drawerData.locked && (
                                        <div style={{ flexShrink: 0 }}>
                                            {(() => {
                                                const score = drawerData.score ?? null;
                                                const { backgroundColor, fillColor } = getElementColors(drawerData.name, drawerData.type);
                                                return (
                                                    <div dangerouslySetInnerHTML={{
                                                        __html: createBipolarProgressBar(score, backgroundColor, fillColor, drawerData.name)
                                                    }} />
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                                {!drawerData.locked && (
                                    <>
                                        <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
                                            {elementDescriptions[drawerData.name]?.sidebar || ''}
                                        </div>
                                        {drawerData.name === 'Price Deals' && (
                                            <Text style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontStyle: 'italic',
                                                color: '#6b7280',
                                                marginTop: '8px'
                                            }}>
                                                Note: This measure ranges from -100% to 0%
                                            </Text>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Locked Element Upgrade UI */}
                            {drawerData.locked ? (
                                <div style={{
                                    padding: '40px 20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    minHeight: '300px',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    alignSelf: 'stretch',
                                    flex: 1
                                }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(156, 163, 175, 0.15)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '24px'
                                    }}>
                                        <LockOutlined style={{
                                            fontSize: '36px',
                                            color: '#9ca3af'
                                        }} />
                                    </div>
                                    <Title level={4} style={{
                                        color: '#374151',
                                        marginBottom: '12px',
                                        fontWeight: 600
                                    }}>
                                        Premium Feature
                                    </Title>
                                    <Text style={{
                                        color: '#6b7280',
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        maxWidth: '280px',
                                        marginBottom: '24px',
                                        display: 'block'
                                    }}>
                                        Upgrade your subscription to unlock detailed insights for <strong>{getDisplayName(drawerData.name)}</strong>, including AI analysis, trends, and constituent elements.
                                    </Text>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<CrownOutlined />}
                                        onClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                                        style={{
                                            background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            height: '44px',
                                            paddingLeft: '24px',
                                            paddingRight: '24px',
                                            fontWeight: 600
                                        }}
                                    >
                                        Upgrade to Unlock
                                    </Button>
                                    <Text style={{
                                        color: '#9ca3af',
                                        fontSize: '12px',
                                        marginTop: '16px',
                                        display: 'block'
                                    }}>
                                        Available with paid plans
                                    </Text>
                                </div>
                            ) : (
                            <div style={{ padding: '0 20px' }}>
                                {/* AI Summary Section */}
                                {(() => {
                                    const displaySummary = currentAiSummary || drawerData.aiSummary || intl.formatMessage({ id: 'brandEquity.aiSummary.awaitingResponses' });
                                    return (
                                        <div style={{
                                            marginBottom: '24px',
                                            padding: '16px',
                                            backgroundColor: '#f8fafc',
                                            borderRadius: '8px',
                                            border: '1px solid #e2e8f0',
                                            borderLeft: '4px solid #3b82f6'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: '8px'
                                            }}>
                                                <span style={{
                                                    fontSize: '16px',
                                                    marginRight: '8px',
                                                    color: '#3b82f6'
                                                }}>
                                                    💭
                                                </span>
                                                <Title level={5} style={{
                                                    color: '#1e293b',
                                                    margin: 0,
                                                    fontSize: '14px',
                                                    fontWeight: '600'
                                                }}>
                                                    First Thoughts
                                                </Title>
                                            </div>
                                            <div style={{
                                                color: '#475569',
                                                fontSize: '14px',
                                                lineHeight: '1.5',
                                                textAlign: 'left'
                                            }}>
                                                <ReactMarkdown
                                                    components={{
                                                        p: ({children}) => <span style={{ display: 'block', marginBottom: '8px' }}>{children}</span>,
                                                        strong: ({children}) => <strong style={{ fontWeight: 600, color: '#1e293b' }}>{children}</strong>,
                                                        em: ({children}) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                        ul: ({children}) => <ul style={{ paddingLeft: '16px', marginBottom: '8px' }}>{children}</ul>,
                                                        ol: ({children}) => <ol style={{ paddingLeft: '16px', marginBottom: '8px' }}>{children}</ol>,
                                                        li: ({children}) => <li style={{ marginBottom: '4px' }}>{children}</li>
                                                    }}
                                                >
                                                    {displaySummary}
                                                </ReactMarkdown>
                                            </div>
                                            <Button
                                                type="default"
                                                size="small"
                                                loading={isReanalyzing}
                                                style={{
                                                    marginTop: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                                onClick={handleReanalyze}
                                            >
                                                {!isReanalyzing && <ReloadOutlined />}
                                                Analyse Again
                                            </Button>
                                        </div>
                                    );
                                })()}

                                {/* Input Elements */}
                                {drawerData.inputs && drawerData.inputs.length > 0 && (
                                    <div style={{ marginBottom: '24px', width: '99%' }}>
                                        <Title level={5} style={{ color: '#1f2937', marginBottom: '12px' }}>
                                            Input Elements ({drawerData.inputs.length})
                                        </Title>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                            {drawerData.inputs.map((input: any, index: number) => {
                                                const inputType = getElementType(input.name);
                                                const inputLocked = isElementLocked(input.name);
                                                const { backgroundColor, fillColor } = inputLocked
                                                    ? { backgroundColor: 'rgba(156, 163, 175, 0.2)', fillColor: '#9ca3af' }
                                                    : getElementColors(input.name, inputType);
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            padding: '12px',
                                                            backgroundColor: inputLocked
                                                                ? 'rgba(156, 163, 175, 0.15)'
                                                                : backgroundColor.replace('0.2)', '0.3)'),
                                                            borderRadius: '6px',
                                                            border: inputLocked ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                                                            width: '100%',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onClick={() => handleElementClick(input.name)}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    fontWeight: '500',
                                                                    color: inputLocked ? '#6b7280' : '#1f2937',
                                                                    marginBottom: '4px'
                                                                }}>
                                                                    {getDisplayName(input.name)}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'center', flexShrink: 0 }}>
                                                                {inputLocked ? (
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '6px',
                                                                        color: '#9ca3af',
                                                                        fontSize: '13px'
                                                                    }}>
                                                                        <LockOutlined style={{ fontSize: '14px' }} />
                                                                        <span>Locked</span>
                                                                    </div>
                                                                ) : (
                                                                    <div dangerouslySetInnerHTML={{
                                                                        __html: createBipolarProgressBar(input.score ?? null, backgroundColor, fillColor, input.name)
                                                                    }} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Output Elements */}
                                {drawerData.outputs && drawerData.outputs.length > 0 && (
                                    <div style={{ width: '99%' }}>
                                        <Title level={5} style={{ color: '#1f2937', marginBottom: '12px' }}>
                                            Feeds Into ({drawerData.outputs.length})
                                        </Title>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                            {drawerData.outputs.map((output: any, index: number) => {
                                                const outputType = getElementType(output.name);
                                                const outputLocked = isElementLocked(output.name);
                                                const { backgroundColor, fillColor } = outputLocked
                                                    ? { backgroundColor: 'rgba(156, 163, 175, 0.2)', fillColor: '#9ca3af' }
                                                    : getElementColors(output.name, outputType);
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            padding: '12px',
                                                            backgroundColor: outputLocked
                                                                ? 'rgba(156, 163, 175, 0.15)'
                                                                : backgroundColor.replace('0.2)', '0.3)'),
                                                            borderRadius: '6px',
                                                            border: outputLocked ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                                                            width: '100%',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onClick={() => handleElementClick(output.name)}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    fontWeight: '500',
                                                                    color: outputLocked ? '#6b7280' : '#1f2937',
                                                                    marginBottom: '4px'
                                                                }}>
                                                                    {getDisplayName(output.name)}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'center', flexShrink: 0 }}>
                                                                {outputLocked ? (
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '6px',
                                                                        color: '#9ca3af',
                                                                        fontSize: '13px'
                                                                    }}>
                                                                        <LockOutlined style={{ fontSize: '14px' }} />
                                                                        <span>Locked</span>
                                                                    </div>
                                                                ) : (
                                                                    <div dangerouslySetInnerHTML={{
                                                                        __html: createBipolarProgressBar(output.score ?? null, backgroundColor, fillColor, output.name)
                                                                    }} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Survey Statements for Measured Elements */}
                                {drawerData.type === 'qc' && (() => {
                                    const qcData = results.questionCategoryScores.find(qc => qc.category === drawerData.name);
                                    const totalResponses = qcData?.input?.reduce((sum, q) => sum + q.responseCount, 0) || 0;
                                    const avgResponses = qcData?.input?.length ? Math.round(totalResponses / qcData.input.length) : 0;

                                    return qcData?.input && qcData.input.length > 0 ? (
                                        <div style={{ marginTop: '24px', width: '100%' }}>
                                            <Title level={5} style={{ color: '#1f2937', marginBottom: '12px' }}>
                                                Survey Statements ({qcData.input.length})
                                            </Title>

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                                {qcData.input.map((question, index) => {
                                                    const isLowResponse = question.responseCount < 20;
                                                    return (
                                                        <div
                                                            key={question.id}
                                                            style={{
                                                                padding: '12px',
                                                                backgroundColor: isLowResponse ? '#fef3c7' : '#f3f4f6',
                                                                borderRadius: '6px',
                                                                border: `1px solid ${isLowResponse ? '#fbbf24' : '#3b82f6'}`,
                                                                width: '100%',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onClick={() => {
                                                                setSelectedStatement({ ...question, polarityPercentage: question.meanScore });
                                                                setStatementModalVisible(true);
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                e.currentTarget.style.boxShadow = 'none';
                                                            }}
                                                        >
                                                            <div style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                    <Text style={{ color: '#6b7280', fontSize: '12px' }}>
                                                                        Statement {index + 1}
                                                                    </Text>
                                                                    <Tag
                                                                        color="blue"
                                                                        style={{
                                                                            fontSize: '10px',
                                                                            lineHeight: '14px',
                                                                            padding: '0 4px',
                                                                            margin: 0,
                                                                            width: 'fit-content'
                                                                        }}
                                                                    >
                                                                        {question.id}
                                                                    </Tag>
                                                                </div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', textAlign: 'right' }}>
                                                                    <Text style={{
                                                                        color: isLowResponse ? '#92400e' : '#6b7280',
                                                                        fontSize: '12px',
                                                                        fontWeight: isLowResponse ? 'bold' : 'normal'
                                                                    }}>
                                                                        {question.responseCount} response{question.responseCount !== 1 ? 's' : ''}
                                                                    </Text>
                                                                    {isLowResponse && (
                                                                        <Tag
                                                                            color="warning"
                                                                            style={{
                                                                                fontSize: '11px',
                                                                                marginRight: -3,  // Override any default margin
                                                                                marginLeft: 'auto'
                                                                            }}
                                                                        >
                                                                            Low Response
                                                                        </Tag>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <Text style={{ color: '#1f2937', display: 'block', marginBottom: '8px', textAlign: 'left' }}>
                                                                {question.text}
                                                            </Text>
                                                            <StrengthSlider
                                                                value={question.meanScore}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Response Count Speedometer */}
                                            <div style={{ marginTop: '20px' }}>
                                                <ResponseSpeedometer responseCount={avgResponses} />
                                            </div>
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                            )}
                        </div>
                    )}
                </Drawer>
            )}

            {/* Statement Detail Modal - for orbital, orbital-v3, and cards visualization with statement selected */}
            {(visualizationType === 'orbital' || visualizationType === 'orbital-v3' || visualizationType === 'cards') && selectedStatement && (
                <StatementDetailModal
                    visible={statementModalVisible}
                    statement={selectedStatement}
                    categoryName={drawerData?.name || selectedStatementCategory}
                    onClose={() => {
                        setStatementModalVisible(false);
                        setSelectedStatement(null);
                    }}
                    theme="light"
                />
            )}
        </div>
    );
};

export default BrandEquityVisualizationWrapper;